import type { ButtonHTMLAttributes, CSSProperties, ReactElement } from 'react';
import { forwardRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { Tooltip } from '@elfsight/widget-modal';
import { Badge, focusVisibleMixin } from '@elfsight/widget-ui';
import {
  createReadableClass,
  getColorWithAlpha,
  getInvertedColor
} from '@elfsight/widget-utils';
import { Slot } from '@radix-ui/react-slot';
import { useFiltersTheme } from '../../filters-theme-provider';
import { flattenColor } from '../../utils';

export type BaseButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  selected?: boolean;
  withOverflow?: boolean;
  withBackground?: boolean;
  backgroundColor?: string;
  buttonStyles?: CSSProperties;
  borderRadius?: number;
  badge?: number;
  leftSlot?: ReactElement;
  rightSlot?: ReactElement;
};

export const BaseButton = forwardRef<HTMLButtonElement, BaseButtonProps>(
  (
    {
      selected = false,
      children,
      onClick,
      leftSlot,
      rightSlot,
      badge,
      borderRadius = 8,
      withOverflow = false,
      withBackground = false,
      backgroundColor,
      buttonStyles,
      ...forwardingProps
    },
    forwardingRef
  ) => {
    const { accentColor, textColor } = useFiltersTheme();
    const [isTextOverflowing, setIsTextOverflowing] = useState(false);
    const hasText = !!children && children.toString().trim().length > 0;
    const buttonBackgroundColor = withBackground
      ? backgroundColor || getInvertedColor(textColor)
      : undefined;

    return (
      <Button
        {...forwardingProps}
        style={buttonStyles}
        $selected={selected}
        $textColor={textColor}
        $accentColor={accentColor}
        $borderRadius={borderRadius}
        $backgroundColor={buttonBackgroundColor}
        $hasText={hasText}
        onClick={onClick}
        ref={forwardingRef}
      >
        {leftSlot && (
          <StyledSlot $edge={hasText ? 'left' : undefined}>
            {leftSlot}
          </StyledSlot>
        )}
        {hasText && (
          <Tooltip title={children} disabled={!isTextOverflowing} offset={16}>
            <Text
              $withOverflow={withOverflow}
              ref={(el) => {
                setIsTextOverflowing(!!el && el.scrollWidth > el.clientWidth);
              }}
            >
              {children}
            </Text>
          </Tooltip>
        )}
        {rightSlot && (
          <StyledSlot $edge={hasText ? 'right' : undefined}>
            {rightSlot}
          </StyledSlot>
        )}
        {!!badge && (
          <AffixBadge color={accentColor} borderMode="circular">
            {badge}
          </AffixBadge>
        )}
      </Button>
    );
  }
);

const AffixBadge = styled(Badge).attrs({
  className: createReadableClass({
    component: 'filter-button',
    element: 'badge'
  })
})`
  position: absolute;
  right: -8px;
  top: -8px;
`;

const StyledSlot = styled(Slot).attrs({
  className: createReadableClass({
    component: 'filter-button',
    element: 'slot'
  })
})<{ $edge?: 'left' | 'right' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  ${({ $edge }) =>
    $edge &&
    {
      left: css`
        margin-right: 8px;
        margin-left: -6px;
      `,
      right: css`
        margin-left: 4px;
        margin-right: -6px;
      `
    }[$edge]}}
`;

const Text = styled.span.attrs({
  className: createReadableClass({
    component: 'filter-button',
    element: 'text'
  })
})<{ $withOverflow?: boolean }>`
  ${({ $withOverflow }) =>
    $withOverflow &&
    css`
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
    `}
`;

const Button = styled.button.attrs({
  className: createReadableClass({
    component: 'filter-button',
    element: 'button'
  })
})<{
  $selected: boolean;
  $borderRadius: number;
  $textColor: string;
  $accentColor: string;
  $hasText?: boolean;
  $backgroundColor?: string;
}>`
  position: relative;
  min-width: 0;
  min-height: 36px;
  cursor: pointer;
  padding: 7px ${({ $hasText }) => ($hasText ? '15px' : '7px')};
  display: flex;
  align-items: center;
  font-size: 14px;
  line-height: 18px;
  font-weight: 500;
  border-style: solid;
  border-width: 1px;
  transition:
    border-color 0.2s,
    background-color 0.2s;

  ${({ $borderRadius }) => css`
    border-radius: ${$borderRadius}px;
  `}

  ${({ $textColor, $backgroundColor, $accentColor }) => css`
    ${focusVisibleMixin($accentColor)}

    color: ${$textColor};
    border-color: ${getColorWithAlpha($textColor, 0.3)};
    background-color: ${flattenColor(
      getColorWithAlpha($textColor, 0),
      $backgroundColor
    )};
    & svg {
      fill: ${$textColor};
    }
  `}

  ${({ $selected, $accentColor, $textColor, $backgroundColor }) => {
    if ($selected) {
      return css`
        border-color: ${$accentColor};
        outline: 1px solid ${$accentColor} !important;
        background-color: ${flattenColor(
          getColorWithAlpha($accentColor, 0.05),
          $backgroundColor
        )};
      `;
    }

    return css`
      &[data-state='open'],
      &:hover,
      &:focus-visible {
        border-color: ${$textColor};
        background-color: ${flattenColor(
          getColorWithAlpha($textColor, 0.02),
          $backgroundColor
        )};
      }
    `;
  }}
`;
